import { useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container } from '@/components/ui/Container';
import { Seo } from '@/components/seo/Seo';
import { submitContactForm } from '@/lib/contactApi';
import { getSessionId, getVisitorId } from '@/lib/analytics';
import type { ContactFormPayload, CtaIntent } from '@/types/cta';

const DEFAULT_MESSAGES: Record<CtaIntent, string> = {
  demo: 'I would like to request a demo.',
  callback: 'I would like to request a callback.',
  contact: 'I would like to get in touch.',
};

function readIntent(value: string | null): CtaIntent {
  return value === 'demo' || value === 'callback' ? value : 'contact';
}

export const ContactPage = (): React.JSX.Element => {
  const [params] = useSearchParams();
  const intent = readIntent(params.get('intent'));
  const sourcePost = params.get('sourcePost') ?? '';
  const sourceTitle = params.get('sourceTitle') ?? '';
  const emailVar = params.get('emailVar') ?? '';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState(DEFAULT_MESSAGES[intent]);
  const [website, setWebsite] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const title = useMemo(() => {
    if (intent === 'demo') return 'Request a demo';
    if (intent === 'callback') return 'Request a callback';
    return 'Contact us';
  }, [intent]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setStatus('submitting');

    const payload: ContactFormPayload = {
      intent,
      name,
      email,
      company,
      phone,
      message,
      sourcePost,
      sourceTitle,
      emailVar: emailVar || undefined,
      website,
      visitorId: getVisitorId(),
      sessionId: getSessionId(),
    };

    try {
      await submitContactForm(payload);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Failed to send your request');
    }
  };

  return (
    <>
      <Seo title={title} description="Get in touch using the contact form." path="/contact" />
      <Container>
        <section className="contact-shell">
          <div className="contact-shell__intro">
            <p className="contact-shell__eyebrow">Contact</p>
            <h1>{title}</h1>
            <p className="contact-shell__dek">
              Send your details and a short note. The request will be delivered to the configured
              contact inbox.
            </p>
            {sourceTitle ? (
              <p className="contact-shell__source">
                Related article: <strong>{sourceTitle}</strong>
              </p>
            ) : null}
          </div>

          {status === 'success' ? (
            <div className="contact-success">
              <h2>Thanks.</h2>
              <p>Your request has been sent. Someone should follow up shortly.</p>
            </div>
          ) : (
            <form className="contact-form" onSubmit={handleSubmit}>
              <label className="contact-form__field">
                <span>Name</span>
                <input value={name} onChange={(e) => { setName(e.target.value); }} required />
              </label>

              <label className="contact-form__field">
                <span>Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); }}
                  required
                />
              </label>

              <label className="contact-form__field">
                <span>Company</span>
                <input value={company} onChange={(e) => { setCompany(e.target.value); }} />
              </label>

              <label className="contact-form__field">
                <span>Phone</span>
                <input value={phone} onChange={(e) => { setPhone(e.target.value); }} />
              </label>

              <label className="contact-form__field">
                <span>Message</span>
                <textarea
                  rows={6}
                  value={message}
                  onChange={(e) => { setMessage(e.target.value); }}
                  required
                />
              </label>

              <label className="contact-form__honeypot" aria-hidden="true">
                <span>Website</span>
                <input
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => { setWebsite(e.target.value); }}
                />
              </label>

              {error ? <p className="contact-form__error">{error}</p> : null}

              <button className="button button--primary" disabled={status === 'submitting'} type="submit">
                {status === 'submitting' ? 'Sending...' : title}
              </button>
            </form>
          )}
        </section>
      </Container>
    </>
  );
};
